// Datos de la pantalla de Vendedores: la tabla del periodo y las ventas del vendedor elegido.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sellersTableApi } from '@/api/services/retail-wizard/backoffice';
import type { SellersTableLakeStatus } from '@/api/services/retail-wizard/backoffice/sellersTableContract';
import { backofficeSalesApi } from '@/api/services/retail-wizard/retail-wizard.client';
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { mxDay } from '@/lib/dateHelpers';
import { reportPeriodRange, type ReportPeriod } from '@/shared/patterns/PeriodBand';
import { usePeriodWindow } from '../../tiendas/api/useTiendas';
import { fromTableRow, withUniqueShortCodes, type SellerRow } from '../lib/sellersTable';
import { trendDays } from '../lib/sellerTrend';

const KEY = ['retail-admin', 'vendedores'] as const;

/** Páginas máximas del listado de ventas del periodo; pasado el tope la ficha avisa. */
const MAX_SALES_PAGES = 10;
const SALES_PAGE_SIZE = 50;

export interface SellersTableData {
    items: SellerRow[];
    truncated: boolean;
    commissionAvailable: boolean;
    commissionPeriod: { from: string; to: string } | null;
    rbacAvailable: boolean;
    /** `null` mientras el back no mande el estado del lake. */
    lake: SellersTableLakeStatus | null;
}

/** La tabla del periodo, en una sola llamada a `GET /backoffice/sellers/table`. */
export function useSellersTable(period: ReportPeriod) {
    const periodWindow = usePeriodWindow(period);
    const table = useQuery({
        queryKey: [...KEY, 'table', periodWindow],
        queryFn: () => sellersTableApi.getTable(periodWindow),
        staleTime: 60_000,
    });

    const data = useMemo((): SellersTableData | undefined => {
        if (!table.data) return undefined;
        const { sources } = table.data;
        const rbacAvailable = sources.rbac === 'OK';
        return {
            items: withUniqueShortCodes(
                table.data.items.map((row) => fromTableRow(row, rbacAvailable)),
            ),
            truncated: table.data.truncated,
            commissionAvailable: sources.commission === 'OK',
            commissionPeriod: table.data.commissionPeriod,
            rbacAvailable,
            lake: sources.lake ?? null,
        };
    }, [table.data]);

    return {
        data,
        error: table.error,
        isPending: table.isPending,
        refetch: () => {
            void table.refetch();
        },
    };
}

export interface SellerSalesResult {
    items: BackofficeSale[];
    /** Hubo más páginas de las que se leyeron. */
    truncated: boolean;
}

async function listAllSales(
    retailerId: string,
    sellerId: string,
    days: { from: string; to: string },
): Promise<SellerSalesResult> {
    const byId = new Map<string, BackofficeSale>();
    let nextToken: string | undefined;
    for (let page = 0; page < MAX_SALES_PAGES; page += 1) {
        const result = await backofficeSalesApi.list(
            { retailerId, sellerId, from: days.from, to: days.to },
            nextToken,
            SALES_PAGE_SIZE,
        );
        for (const sale of result.items) byId.set(sale.saleId, sale);
        // Solo la falta de token es el final; una página vacía con token no lo es (filtro del back).
        if (!result.nextToken) return { items: [...byId.values()], truncated: false };
        nextToken = result.nextToken;
    }
    return { items: [...byId.values()], truncated: true };
}

/** Ventas del vendedor entre dos días (CDMX), hasta el tope; la clave la comparten las pestañas. */
function useSellerSales(
    sellerId: string | null,
    retailerId: string | null,
    days: { from: string; to: string },
    enabled: boolean,
) {
    return useQuery({
        queryKey: [...KEY, 'period-sales', retailerId, sellerId, days],
        queryFn: () => listAllSales(retailerId ?? '', sellerId ?? '', days),
        enabled: enabled && sellerId !== null && retailerId !== null,
        staleTime: 60_000,
    });
}

/** Ventas del vendedor en la ventana del periodo; las leen Desempeño (mix) y Ventas. */
export function useSellerPeriodSales(
    sellerId: string | null,
    retailerId: string | null,
    period: ReportPeriod,
    enabled = true,
) {
    const today = mxDay(new Date().toISOString());
    const days = useMemo(
        () => {
            const { fromDay, toDay } = reportPeriodRange(period);
            return { from: fromDay, to: toDay };
        },
        // `today` rehace la ventana cuando cambia el día.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [period, today],
    );
    return useSellerSales(sellerId, retailerId, days, enabled);
}

/** Ventas del vendedor de las últimas 8 semanas, para la tendencia; no sigue el periodo. */
export function useSellerTrendSales(
    sellerId: string | null,
    retailerId: string | null,
    enabled = true,
) {
    const today = mxDay(new Date().toISOString());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` rehace la ventana al cambiar el día
    const days = useMemo(() => trendDays(new Date()), [today]);
    return useSellerSales(sellerId, retailerId, days, enabled);
}
